import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { ticketParticipantsSet, ticketUpdate } from '~/actions/ticket';
import { getTicketParticipants } from '~/api/tickets';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import * as toastTypes from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';
import { getClientInfo, getUserClientInfo } from '~/utils/user';

function MarkCompleteAction({
  disabled,
  // connected
  ticketId,
  ticketUpdate,
  ticketParticipantsSet,
}) {
  const currentUser = useCurrentUser();
  const intl = useIntl();

  const [completeTicket] = api.endpoints.completeTicket.useMutation();

  const [modal, showModal] = useModal({
    title: 'Complete ticket',
    actionButton: {
      text: 'Submit',
      variant: 'primary',
      errorHandler: () =>
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'MarkCompleteAction.ticketCompletionFailed',
            defaultMessage:
              'Ticket completion failed. Please refresh the page and try again.',
            description: 'Error message when ticket completion fails',
          }),
        ),
      promise: async () => {
        const res = await completeTicket({ ticketId }).unwrap();
        trackSegment('Workflow is completed', {
          groupId: getClientInfo(currentUser),
          userId: getUserClientInfo(currentUser),
          ticketId,
        });
        const { data: participants } = await getTicketParticipants({
          ticketId,
        });
        const updatedTicket = {
          document: res.document,
          isCompleted: res.isCompleted,
          modifiedDate: res.modifiedDate,
          stages: res.stages,
          status: res.status,
        };
        ticketUpdate(updatedTicket);
        ticketParticipantsSet(participants);
      },
    },
    content: (
      <FlexLayout flexDirection="column" space={8}>
        <FlexLayout flexDirection="column" space={2}>
          <Text variant="xs-dense">
            <FormattedMessage
              id="MarkCompleteAction.markCompleteConfirmation"
              defaultMessage="Are you sure you want to complete this ticket?"
              description="Confirmation message for marking a ticket as complete"
            />
          </Text>
          <Text variant="xs-dense-bold">
            <FormattedMessage
              id="MarkCompleteAction.markCompleteWarning"
              defaultMessage="You won’t be able to undo this action."
              description="Warning message for marking a ticket as complete"
            />
          </Text>
        </FlexLayout>
      </FlexLayout>
    ),
  });

  return (
    <>
      <Button
        disabled={disabled}
        text={intl.formatMessage({
          id: 'MarkCompleteAction.markAsComplete',
          defaultMessage: 'Mark as Complete',
          description: 'Button text for marking a ticket as complete',
        })}
        onClick={showModal}
        variant="primary"
      />
      {modal}
    </>
  );
}

function mapStateToProps({ ticket }) {
  return { ticketId: ticket.id };
}

export default connect(mapStateToProps, {
  ticketUpdate,
  ticketParticipantsSet,
})(MarkCompleteAction);
